// Пуловая колонка ряда входов: набранные плитки плюс одна кнопка добавления.
// Ролей у такого канала нет — он принимает до N файлов, и порядок в нём не несёт
// смысла, поэтому решётки пустых слотов здесь нет: у Seedance лимит доходит до
// девяти, и пустая решётка заняла бы бар целиком, ничего не сообщая.
//
// Одним компонентом живут три канала — референсы, входное видео и входной звук.
// Различаются они только тем, что принимают, какими словами подписаны и откуда
// можно брать файл: у звука нет ни библиотеки загруженного, ни генераций.
//
// Канал может быть объявлен моделью, но неисполним нашим маршрутом. Тогда кнопка
// рисуется неактивной и прямо говорит почему: показать возможность, которой у
// маршрута нет, честнее, чем принять файл, который молча никуда не уедет.
// Причину считает `prompt-channel-gates`, слот только показывает.

import { ImagePlus, Layers, Plus, Sparkles } from "lucide-react";
import { useRef, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";
import { MediaLightbox } from "~/shared/ui/media-lightbox";

import { AttachmentThumbBody, AttachmentThumbCaption } from "./AttachmentThumb";
import { attachmentThumbFrame } from "./attachment-thumb-frame";
import { SlotColumn } from "./SlotColumn";
import { usePendingLightbox } from "./usePendingLightbox";

import type { PendingUpload } from "~/shared/store/types";

export interface MediaChannelSlotsProps {
    /** Сколько файлов принимает канал при текущем режиме. */
    max: number;
    /** Уже набранное этим каналом. */
    items: readonly PendingUpload[];
    disabled: boolean;
    /** Что принимает `<input type="file">` этого канала. */
    accept?: string;
    /** Готовая подсказка «почему недоступно»; пусто — канал доступен. */
    unavailableHint?: string;
    /** Подпись кнопки добавления — своя у каждого канала. */
    addLabel: string;
    /** Подпись кнопки «убрать» на плитке. */
    removeLabel: string;
    onPickFiles: (files: File[]) => void;
    /** Не задан — источника «из загруженных» у канала нет (звук). */
    onPickFromUploaded?: () => void;
    /** Не задан — источника «из сгенерированного» у канала нет (звук). */
    onPickFromGenerated?: () => void;
    onRemove: (localId: string) => void;
}

export function MediaChannelSlots({
    max,
    items,
    disabled,
    accept,
    unavailableHint,
    addLabel,
    removeLabel,
    onPickFiles,
    onPickFromUploaded,
    onPickFromGenerated,
    onRemove,
}: MediaChannelSlotsProps) {
    const { t } = useTranslation("chat");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const lightbox = usePendingLightbox(items);

    if (max <= 0) return null;

    const hasRoom = items.length < max;
    const blocked = disabled || unavailableHint !== undefined;
    // Библиотеки у канала может не быть вовсе — тогда меню источников не нужно,
    // кнопка сразу открывает выбор файла.
    const hasLibrarySources = onPickFromUploaded !== undefined || onPickFromGenerated !== undefined;

    const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) onPickFiles(Array.from(files));
        event.target.value = "";
    };

    const addButtonClass =
        "flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-white/15 bg-zinc-900/60 text-zinc-500 transition-colors hover:border-white/30 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-40";

    return (
        <div className="flex flex-wrap items-start gap-3">
            {items.map((item) => (
                // Подписи роли у пулового канала нет, но её строка резервируется:
                // иначе плитки этого канала встают выше кадров и ряд разъезжается.
                <SlotColumn key={item.localId}>
                    <div
                        className={cn(attachmentThumbFrame(item.status === "error"), "h-16 w-16")}
                        title={item.status === "error" ? (item.error ?? item.fileName) : item.fileName}
                    >
                        <AttachmentThumbBody
                            item={item}
                            onRemove={onRemove}
                            removeLabel={removeLabel}
                            playLabel={t("prompt.attachmentPlay")}
                            pauseLabel={t("prompt.attachmentPause")}
                            onZoom={lightbox.openAt}
                            zoomLabel={t("prompt.attachmentZoom")}
                        />
                    </div>
                    <AttachmentThumbCaption item={item} />
                </SlotColumn>
            ))}

            {hasRoom && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={accept ?? "image/*"}
                        multiple
                        hidden
                        onChange={handleFiles}
                    />
                    {hasLibrarySources ? (
                        <SlotColumn>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        disabled={blocked}
                                        aria-label={addLabel}
                                        title={unavailableHint}
                                        className={addButtonClass}
                                    >
                                        <Plus className="h-5 w-5" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-56">
                                    <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                                        <ImagePlus className="mr-2 h-4 w-4" />
                                        {t("prompt.attachMenu.uploadFile")}
                                    </DropdownMenuItem>
                                    {onPickFromUploaded && (
                                        <DropdownMenuItem onClick={onPickFromUploaded}>
                                            <Layers className="mr-2 h-4 w-4" />
                                            {t("prompt.attachMenu.fromUploaded")}
                                        </DropdownMenuItem>
                                    )}
                                    {onPickFromGenerated && (
                                        <DropdownMenuItem onClick={onPickFromGenerated}>
                                            <Sparkles className="mr-2 h-4 w-4" />
                                            {t("prompt.attachMenu.fromGenerated")}
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SlotColumn>
                    ) : (
                        <SlotColumn>
                            <button
                                type="button"
                                disabled={blocked}
                                aria-label={addLabel}
                                title={unavailableHint}
                                onClick={() => fileInputRef.current?.click()}
                                className={addButtonClass}
                            >
                                <Plus className="h-5 w-5" />
                            </button>
                        </SlotColumn>
                    )}
                </>
            )}

            <MediaLightbox
                items={lightbox.items}
                initialIndex={lightbox.initialIndex}
                isOpen={lightbox.isOpen}
                onClose={lightbox.close}
            />
        </div>
    );
}
